'use strict';

var React = require('react');
var reactUtils = require('@sravni/react-utils');
var sizes = require('../../../constants/sizes.js');
var Space = require('../../Space/src/Space.js');
var Accordion_module = require('../styles/Accordion.module.less.js');
var AccordionContext = require('./AccordionContext.js');
require('../types/Accordion.types.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/** Helper function normalizes indexes */
const normalizeIndexes = (children, index) => {
    if (Array.isArray(index)) {
        return index;
    }
    if (index === '*') {
        const index = React__namespace.Children.map(children, (_, i) => i) || [];
        return index;
    }
    return [];
};
/**
 * @description A content area which can be collapsed and expanded
 *
 * @component
 * @example
 * ```jsx
 * <Accordion activeKeys={['credit']}>
 *   <Accordion.Item key="credit" title="О кредите">content</Accordion.Item>
 * </Accordion>
 * ```
 */
const Accordion = React__namespace.forwardRef((props, ref) => {
    const { index, defaultIndex, separator, background, padding, scrollIntoView, children, className, onChange, size = sizes.ACCORDION_SIZES.S16, ...restProps } = props;
    const innerChildren = React__namespace.useMemo(() => React__namespace.Children.toArray(children).filter(Boolean), [children]);
    const isFirstRender = React__namespace.useRef(true);
    const [innerValue, setInnerValue] = reactUtils.useStateCallback(normalizeIndexes(innerChildren, index ?? defaultIndex));
    const cx = reactUtils.useStyles(Accordion_module.default);
    const classNames = cx(className, {
        [`separator-${separator}`]: separator,
        [`background-${background}`]: background,
    });
    const handleCollapse = React__namespace.useCallback((event) => {
        const value = Number(event.currentTarget.dataset.index);
        const pos = innerValue.indexOf(value);
        const indexes = pos >= 0 ? reactUtils.removeIndex(innerValue, pos) : reactUtils.addItem(innerValue, value);
        setInnerValue(indexes, (val) => {
            onChange?.(val || []);
        });
    }, [innerValue, onChange, setInnerValue]);
    const panels = React__namespace.useMemo(() => React__namespace.Children.map(innerChildren, (child, index) => {
        const childProps = {
            ...child.props,
            id: index,
            active: innerValue.indexOf(index) >= 0,
        };
        return React__namespace.cloneElement(child, childProps);
    }), [innerValue, innerChildren]);
    const accordionContextProviderValue = React__namespace.useMemo(() => ({ scrollIntoView, padding, onCollapse: handleCollapse, size }), [handleCollapse, padding, scrollIntoView, size]);
    const separatorSpace = separator === 'space' ? accordionContextProviderValue.size : 0;
    React__namespace.useEffect(() => {
        if (isFirstRender.current) {
            const indexes = normalizeIndexes(innerChildren, index ?? defaultIndex);
            setInnerValue(indexes);
            isFirstRender.current = false;
        }
        else {
            setInnerValue((prevIndex) => (reactUtils.isDefined(index) ? normalizeIndexes(innerChildren, index) : prevIndex));
        }
    }, [innerChildren, index, defaultIndex, setInnerValue]);
    return (React__namespace.createElement(Space.Space, { ref: ref, "data-qa": "Accordion", size: separatorSpace, direction: "vertical", className: classNames, ...restProps },
        React__namespace.createElement(AccordionContext.default.Provider, { value: accordionContextProviderValue }, panels)));
});
Accordion.defaultProps = {
    separator: 'space',
    scrollIntoView: true,
    padding: true,
};
Accordion.displayName = 'Accordion';

exports.Accordion = Accordion;
//# sourceMappingURL=Accordion.js.map
